import PropTypes from 'prop-types';
import { FormProvider } from 'react-hook-form';
import { equals, isEmpty, not } from 'ramda';
import { Icon, PageSubtitle, PageTitle } from '@fasstech/middle-design-system';
import { PageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer.jsx';
import TabsPagination from '@@viewCommonComponents/TabsPagination.jsx';
import CommissionRateSelector from '@@views/simulationPricingGroupHealth/client/components/CommissionRateSelector.jsx';
import PricingSelector from '@@views/simulationPricingGroupHealth/client/components/PricingSelector.jsx';
import useDisplayedPricing from '@@views/simulationPricingGroupHealth/client/hooks/useDisplayedPricings.js';
import FormulaHeader from './components/FormulaHeader.jsx';
import Options from './components/Options.jsx';
import useSimulationWarrantiesGroupProtectionForm from './hooks/useSimulationWarrantiesGroupProtectionForm.js';

const NUMBER_OF_DISPLAYED_PRICINGS = 4;

// Vue « Garanties » de la prévoyance collective : grille des formules, options et taux de commission (offre non modulaire)
const SimulationWarrantiesGroupProtectionForm = ({
  getSimulationWarrantiesGroupProtection,
  getMiddleText,
  getMiddleNumber,
  getMiddleBoolean
}) => {
  const {
    form,
    isApplicable,
    pricings,
    options,
    commissionRates,
    maxNumberOfProducts,
    isSelected,
    selectedPricingCodes,
    onSelectPricings,
    selectedOptions,
    toggleOption,
    isSelectionComplete,
    onSubmit,
    $onClickPrev
  } = useSimulationWarrantiesGroupProtectionForm({
    getSimulationWarrantiesGroupProtection,
    getMiddleNumber
  });

  const { displayedPricings, updatePricingsTabsPagination, isPricingsTabsButtonDisabled } =
    useDisplayedPricing(pricings, NUMBER_OF_DISPLAYED_PRICINGS, false);

  // Taux affiché selon le seul flag CMS : le ciblage passe par les critères du moteur
  const isCommissionDisplayed =
    getMiddleBoolean('PROCESSDEVENTE_TAUX_COMMISSION_EST_NECESSAIRE') && !isEmpty(commissionRates);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <PageLayout withFormLayout={false} className={'flex flex-col items-center w-full gap-6'}>
          <Icon name="circleUmbrella" size={100} />
          <PageTitle>{getMiddleText('NIVEAUXGARANTIESETTARIFS_GENERAL_VIEW_TITLE')}</PageTitle>
          <PageSubtitle>
            {equals(maxNumberOfProducts, 1)
              ? getMiddleText('NIVEAUXGARANTIESETTARIFS_GENERAL_DESCRIPTION_SINGULAR')
              : getMiddleText('NIVEAUXGARANTIESETTARIFS_GENERAL_DESCRIPTION_PLURAL', {
                numberOfProducts: maxNumberOfProducts
              })}
          </PageSubtitle>
          {isApplicable && (
            <div className={'flex flex-col items-center w-full gap-6'}>
              <div className={'w-full overflow-visible'}>
                {isCommissionDisplayed && (
                  <CommissionRateSelector rates={commissionRates} getMiddleText={getMiddleText} />
                )}
                {pricings.length > NUMBER_OF_DISPLAYED_PRICINGS && (
                  <TabsPagination
                    className="visible flex justify-end gap-4"
                    updateVisibility={updatePricingsTabsPagination}
                    isButtonDisabled={isPricingsTabsButtonDisabled}
                  />
                )}
                <FormulaHeader displayedPricings={displayedPricings} isSelected={isSelected} />
                <PricingSelector
                  displayedPricings={displayedPricings}
                  selectedPricingCodes={selectedPricingCodes}
                  onSelect={onSelectPricings}
                  maxNumberOfProducts={maxNumberOfProducts}
                />
                <Options
                  getMiddleText={getMiddleText}
                  displayedPricings={displayedPricings}
                  options={options}
                  isSelected={isSelected}
                  selectedOptions={selectedOptions}
                  toggleOption={toggleOption}
                />
              </div>
            </div>
          )}
        </PageLayout>
        <ButtonContainer
          $onPreviousButtonClick={$onClickPrev}
          getMiddleText={getMiddleText}
          nextButtonLabel={getMiddleText('GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL')}
          nextButtonIsDisabled={isApplicable && not(isSelectionComplete)}
        />
      </form>
    </FormProvider>
  );
};

SimulationWarrantiesGroupProtectionForm.propTypes = {
  getSimulationWarrantiesGroupProtection: PropTypes.object,
  getMiddleText: PropTypes.func,
  getMiddleNumber: PropTypes.func,
  getMiddleBoolean: PropTypes.func
};

export default SimulationWarrantiesGroupProtectionForm;
